import mapboxgl from 'mapbox-gl';
import { useEffect, useMemo, useRef } from 'react';
import type { GeoJSONPolygon } from '@rlpscout/shared/contracts';
import type { CandidateProperty } from '@/lib/api-types';
import { cn } from '@/lib/cn';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN ?? '';
const MAP_STYLE = 'mapbox://styles/mapbox/light-v11';
const POLY_FILL = '#0d9488';
const POLY_LINE = '#115e59';

const PILL_BASE =
  'flex h-8 min-w-8 place-items-center justify-center rounded-full border-2 px-2 font-sans text-[0.6875rem] font-bold transition-all shadow-[0_2px_8px_rgba(15,23,42,0.15)]';
const PILL_DEFAULT = `${PILL_BASE} border-slate-900 bg-slate-900 text-white`;
const PILL_SELECTED = `${PILL_BASE} border-white bg-brand-500 text-slate-950 scale-110`;

export function PolygonMap({
  polygon,
  candidates,
  selectedId,
  onSelect,
  className,
}: {
  polygon: GeoJSONPolygon | null;
  candidates: CandidateProperty[];
  selectedId?: string | null;
  onSelect?: (id: string) => void;
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<Map<string, mapboxgl.Marker>>(new Map());

  const center = useMemo(() => centerOfCandidates(candidates, polygon), [candidates, polygon]);

  useEffect(() => {
    if (!containerRef.current) return;
    if (!MAPBOX_TOKEN) return;
    mapboxgl.accessToken = MAPBOX_TOKEN;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: MAP_STYLE,
      center,
      zoom: 10,
      attributionControl: true,
    });
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      const src = map.getSource('delineated-area') as mapboxgl.GeoJSONSource | undefined;
      const data = polygon
        ? { type: 'Feature' as const, geometry: polygon, properties: {} }
        : { type: 'FeatureCollection' as const, features: [] };
      if (src) {
        src.setData(data);
      } else {
        map.addSource('delineated-area', { type: 'geojson', data });
        map.addLayer({
          id: 'delineated-area-fill',
          type: 'fill',
          source: 'delineated-area',
          paint: { 'fill-color': POLY_FILL, 'fill-opacity': 0.08 },
        });
        map.addLayer({
          id: 'delineated-area-line',
          type: 'line',
          source: 'delineated-area',
          paint: {
            'line-color': POLY_LINE,
            'line-width': 1.25,
            'line-dasharray': [3, 2],
          },
        });
      }
      if (polygon) {
        const [minX, minY, maxX, maxY] = bboxOfPolygon(polygon);
        map.fitBounds([[minX, minY], [maxX, maxY]], { padding: 48, animate: false });
      }
    };
    if (map.isStyleLoaded()) apply();
    else map.once('load', apply);
  }, [polygon]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const existing = markersRef.current;
    const nextIds = new Set(candidates.map((c) => c.id));
    for (const [id, marker] of existing) {
      if (!nextIds.has(id)) {
        marker.remove();
        existing.delete(id);
      }
    }
    for (const c of candidates) {
      let marker = existing.get(c.id);
      if (!marker) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.setAttribute('data-testid', `marker-${c.id}`);
        btn.setAttribute('aria-label', `Candidate at ${c.address}`);
        btn.className =
          'group grid place-items-center -translate-x-1/2 -translate-y-1/2 cursor-pointer';
        const pill = document.createElement('span');
        pill.className = PILL_DEFAULT;
        btn.appendChild(pill);
        btn.addEventListener('click', () => onSelect?.(c.id));
        marker = new mapboxgl.Marker({ element: btn }).setLngLat([c.longitude, c.latitude]);
        marker.addTo(map);
        existing.set(c.id, marker);
      } else {
        marker.setLngLat([c.longitude, c.latitude]);
      }
      const btn = marker.getElement() as HTMLButtonElement;
      const pill = btn.firstElementChild as HTMLSpanElement | null;
      if (pill) {
        const score = c.match_score ?? 0;
        pill.textContent = String(score);
        pill.className = c.id === selectedId ? PILL_SELECTED : PILL_DEFAULT;
      }
    }
  }, [candidates, selectedId, onSelect]);

  if (!MAPBOX_TOKEN) {
    return (
      <div
        className={cn(
          'grid place-items-center rounded-xl border border-dashed border-rule-strong bg-surface-sunk p-10 text-sm text-ink-muted',
          className
        )}
      >
        Set <code className="mx-1 rounded bg-surface px-1.5 py-0.5 font-mono text-ink">VITE_MAPBOX_TOKEN</code> to enable cartography.
      </div>
    );
  }

  return (
    <div className={cn('overflow-hidden rounded-xl border border-rule bg-surface shadow-card', className)}>
      <div ref={containerRef} data-testid="polygon-map" className="h-[28rem] w-full" />
    </div>
  );
}

function centerOfCandidates(
  candidates: CandidateProperty[],
  polygon: GeoJSONPolygon | null
): [number, number] {
  if (candidates.length > 0) {
    const lng = candidates.reduce((s, c) => s + c.longitude, 0) / candidates.length;
    const lat = candidates.reduce((s, c) => s + c.latitude, 0) / candidates.length;
    return [lng, lat];
  }
  if (polygon) {
    const [minX, minY, maxX, maxY] = bboxOfPolygon(polygon);
    return [(minX + maxX) / 2, (minY + maxY) / 2];
  }
  return [-77.0369, 38.9072];
}

function bboxOfPolygon(poly: GeoJSONPolygon): [number, number, number, number] {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const ring of poly.coordinates) {
    for (const [x, y] of ring) {
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  return [minX, minY, maxX, maxY];
}
